import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Assessment } from 'types/api/envScales/Assessment';
import { scoreToRange, IndicatorScoreValue } from 'helpers/dimension';
import {
  DimensionScore,
  ObservationCycle
} from 'types/api/envScales/ObservationCycle';

export interface CycleColumn {
  id: number;
  cycleNumber: number;
}

export interface IndicatorRow {
  id: number;
  name: string;
  t_name: string;

  /** Indicator score for each cycle, `null` when not scored. */
  scores: IndicatorScoreValue[];
}

export interface DimensionRow {
  id: number;
  name: string;
  t_name: string;
  abbreviation: string;

  /** Dimension score for each cycle, `null` when not scored. */
  scores: (number | null)[];
  indicators: IndicatorRow[];
}

export interface DomainGroup {
  id: number;
  name: string;
  t_name: string;
  abbreviation: string;
  dimensions: DimensionRow[];
}

function sortedCycles(assessment: Assessment): ObservationCycle[] {
  const cycles = assessment.observation?.observation_cycles ?? [];
  return [...cycles].sort((a, b) => a.cycle_number - b.cycle_number);
}

/** Observation cycles in cycle number order. */
export function getCycleColumns(assessment: Assessment): CycleColumn[] {
  return sortedCycles(assessment).map(cycle => ({
    id: cycle.id,
    cycleNumber: cycle.cycle_number
  }));
}

function findDimensionScore(
  cycle: ObservationCycle,
  dimensionId: number
): DimensionScore | undefined {
  return cycle.dimension_scores?.find(
    score => score.dimension_id === dimensionId
  );
}

function findIndicatorScore(
  dimensionScore: DimensionScore | undefined,
  indicatorId: number
): IndicatorScoreValue {
  const score = dimensionScore?.indicator_scores?.find(
    indicatorScore => indicatorScore.indicator_id === indicatorId
  );

  return score?.score ?? null;
}

/**
 * Builds the table rows for the scores by cycle table: domains, each holding
 * its dimensions, each holding its indicators, with one score per cycle.
 *
 * Scores are read from the expanded assessment payload, where every cycle's
 * `dimension_scores` carries its own `indicator_scores`. A score that is
 * absent from the payload is `null`, meaning it has not been scored.
 */
export function getScoresByDomain(
  ageLevel: AgeLevel,
  assessment: Assessment
): DomainGroup[] {
  const cycles = sortedCycles(assessment);

  return ageLevel.domains.map(domain => ({
    id: domain.id,
    name: domain.name,
    t_name: domain.t_name,
    abbreviation: domain.abbreviation,
    dimensions: domain.dimensions.map(dimension => {
      const dimensionScores = cycles.map(cycle =>
        findDimensionScore(cycle, dimension.id)
      );

      return {
        id: dimension.id,
        name: dimension.name,
        t_name: dimension.t_name,
        abbreviation: dimension.abbreviation,
        scores: dimensionScores.map(score => score?.dimension_score ?? null),
        indicators: (dimension.indicators ?? []).map(indicator => ({
          id: indicator.id,
          name: indicator.name,
          t_name: indicator.t_name,
          scores: dimensionScores.map(dimensionScore =>
            findIndicatorScore(dimensionScore, indicator.id)
          )
        }))
      };
    })
  }));
}

/**
 * Formats a dimension score for display: a range label for informal
 * observations, the score itself otherwise. Returns `null` when not scored.
 */
export function formatDimensionScore(
  score: number | null,
  isInformal: boolean
): string | null {
  if (score === null) {
    return null;
  }

  return isInformal ? scoreToRange(score) : String(score);
}

/**
 * A cycle is only partly scored when it has been started and left unfinished:
 * some indicator scored but not all of them, or indicators without the
 * dimension score that goes with them. A cycle nobody has touched is empty, not
 * partial — which is why this is judged per cycle rather than across them all.
 */
function isCyclePartiallyScored(
  dimension: DimensionRow,
  cycleIndex: number
): boolean {
  const indicators = dimension.indicators.map(
    indicator => indicator.scores[cycleIndex]
  );

  const scoredIndicators = indicators.filter(score => score !== null).length;
  const hasDimensionScore = dimension.scores[cycleIndex] !== null;

  if (!hasDimensionScore && scoredIndicators === 0) {
    return false;
  }

  return !hasDimensionScore || scoredIndicators < indicators.length;
}

/** Dimensions to expand on first load: those with a cycle left half-scored. */
export function getPartiallyScoredDimensionIds(
  domains: DomainGroup[]
): number[] {
  return domains.flatMap(domain =>
    domain.dimensions
      .filter(
        dimension =>
          dimension.indicators.length > 0 &&
          dimension.scores.some((_, cycleIndex) =>
            isCyclePartiallyScored(dimension, cycleIndex)
          )
      )
      .map(dimension => dimension.id)
  );
}

/** Every dimension id in the table, used by the expand all control. */
export function getAllDimensionIds(domains: DomainGroup[]): number[] {
  return domains.flatMap(domain =>
    domain.dimensions.map(dimension => dimension.id)
  );
}
